import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tokens } from '@kcftech/design-system';
import { TileStyles } from 'components/tiles/Tile';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

const tileGridStyles = css`
    text-decoration: none;

    display: grid;
    grid-template-areas:
        'icon title'
        'icon subtitle';
    grid-template-rows: 2.4rem auto;
    grid-template-columns: 4.2rem 1fr;

    padding-inline: 0.8rem;
    padding-block: 1.2rem;

    gap: 1rem;
    row-gap: 0.4rem;

    align-items: center;

    .icon {
        grid-area: icon;
        align-self: center;
        justify-self: center;
    }
    .title {
        grid-area: title;
        font-size: 1.6rem;
    }
    &:hover .title {
        text-decoration: underline;
    }
    .subtitle {
        grid-area: subtitle;
        color: ${tokens.text.secondary};
        font-size: 1.2rem;
        align-self: start;
    }
`;
const StyledTile = styled(Link)`
    ${TileStyles}
    ${tileGridStyles}
`;

const GradientBgTile = styled(Link)`
    ${TileStyles}
    ${tileGridStyles}

    position: relative;
    background: radial-gradient(ellipse 120% 80% at 30% 50%, #0a1633 0%, #163060 35%, #1a5276 60%, #66cbe3 100%);
    background-attachment: fixed;
    color: #fff;
    border: none !important;
    outline: 1px solid ${tokens.border.default};
    transition: outline-color 0.3s ease, box-shadow 0.3s ease;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, #0a1633 0%, #163060 40%, #1a5276 70%, #66cbe3 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
        z-index: 0;
        border-radius: 8px;
    }

    /* push real content above the pseudo-element */
    > * {
        position: relative;
        z-index: 1;
    }

    .subtitle {
        color: rgba(255, 255, 255, 0.75);
    }

    &:hover {
        outline: 1px solid #66cbe3;
        box-shadow: inset 0 0 20px rgba(102, 203, 227, 0.15),
            0 0 15px rgba(102, 203, 227, 0.3);

        &::before {
            opacity: 1;
        }
    }

    &:active {
        outline: 2px solid #66cbe3;
    }
`;

const GradientIconTile = styled(Link)`
    ${TileStyles}
    ${tileGridStyles}

    .title {
        background: linear-gradient(135deg, #0a1633 0%, #66cbe3 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }
`;

export function SvgGradientDefs() {
    return (
        <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
                <linearGradient id="sd-blue-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0a1633" />
                    <stop offset="100%" stopColor="#66cbe3" />
                </linearGradient>
            </defs>
        </svg>
    );
}

type TileVariant = 'default' | 'gradient-bg' | 'gradient-icon';

type DashboardTileProps = {
    title: string;
    subtitle: string;
    navigateTo: string;
    icon: IconDefinition;
    variant?: TileVariant;
};

const tileComponents: Record<TileVariant, typeof StyledTile> = {
    default: StyledTile,
    'gradient-bg': GradientBgTile,
    'gradient-icon': GradientIconTile,
};

export function DashboardTile({
    title,
    subtitle,
    navigateTo,
    icon,
    variant = 'default',
}: DashboardTileProps): JSX.Element {
    const TileComponent = tileComponents[variant];
    const iconRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (variant !== 'gradient-icon' || !iconRef.current) {
            return;
        }
        iconRef.current
            .querySelectorAll('svg path')
            .forEach((p) => p.setAttribute('fill', 'url(#sd-blue-gradient)'));
    });

    return (
        <TileComponent to={navigateTo}>
            <span ref={iconRef} className="icon">
                <FontAwesomeIcon icon={icon} fontSize={24} />
            </span>
            <div className="title bold word-wrap-anywhere">{title}</div>
            <div className="subtitle">{subtitle}</div>
        </TileComponent>
    );
}
